import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ActionMenuItem, BaseActionEnum } from "../action-menu/ActionMenuItem";
import ContextualAppBar from "../contextual-app-bar/ContextualAppBar";
import NoData from "../no-data/NoData";
import TabBar from "../tab-bar/TabBar";
import TabContainer from "../tab-container/TabContainer";
import TabWithCount from "../tab-with-count/TabWithCount";
import { useAvailableSummaryListingTabs } from "../../queries/available-views/UseAvailableSummaryListingTabs";
import SummaryListingDataService from "../../services/summary-listing-data-service/SummaryListingDataService";
import AlarmLynxView, { AlarmLynxViewType } from "./alarmlynx-view/AlarmLynxView";
import CompressorLynxView, { CompressorLynxViewType } from "./compressorlynx-view/CompressorLynxView";
import DevicesView, { DevicesViewType } from "./devices-view/DevicesView";
import { useSummaryListingContext } from "./SummaryListingContext";
import TankBatteryView, { TankBatteryViewType } from "./tank-battery-view/TankBatteryView";
import WellsView, { WellsViewType } from "./wells-view/WellsView";
import { ApplicationMimeTypesEnum } from "../../common/enums/ApplicationMimeTypesEnum";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { IExportOptions } from "../../services/summary-listing-data-service/BaseModels";
import { useProgressContext } from "../../contexts/progress-context/ProgressContext";
import { ActionTypeEnum, StatusEnum } from "../../common/IExportNotificationMsg";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { useObjectViewState } from "../../queries/object-view-state/UseObjectViewState";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { AppNotificationTypeEnum } from "../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../contexts/app-notification-context/UseAppNotificationContext";
import { ViewOptionStateEnum } from "../contextual-app-bar/ViewOptionStateEnum";
import UUID from "../../common/utilities/Uuid";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { RestError } from "../../services/rest-service/RestService";
import { isMobile, isTablet } from "react-device-detect";
import { ISummaryListingDashboardComponent } from "../../services/dashboard-data-service/Models";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { Divider, Stack, useTheme } from "@mui/material";
import { TabEnum } from "./SummaryListingViewUtils";
import { Refresh } from "@mui/icons-material";
import { RefreshIcon } from "../action-menu/ActionMenuIcons";


export type SummaryListingViewContentBodyProps = {
    title?: string;
    groupID: number,
    contextObject: IeLynxObject,
    configurationID?: number,
    dense?: boolean;
    onExecuteAction?: (action: ActionMenuItem) => void;
    maximize?: boolean;
    designMode?: boolean;
    dashboardComponent?: ISummaryListingDashboardComponent;
    activeTab: TabEnum;
    setActiveTab: (tab: TabEnum, force?: boolean) => void;

}


export default function SummaryListingViewContentBody({
    title,
    groupID,
    contextObject, 
    configurationID,
    dense,
    onExecuteAction,
    maximize,
    designMode,
    dashboardComponent,
    activeTab,
    setActiveTab

}: SummaryListingViewContentBodyProps) {

    const isPhone = isMobile && !isTablet;
    
    //const routeContext = useRouteContext();
    const [userState] = useUserContext();
    const [progressState, actionStarted] = useProgressContext();
    const [offlineState] = useOfflineDetectContext();    
    const [, setAppNotificationState] = useAppNotificationContext();    
    const theme = useTheme();

    const devicesView = useRef<DevicesViewType>(undefined);
    const wellsView = useRef<WellsViewType>(undefined);
    const tankBatteryView = useRef<TankBatteryViewType>(undefined);
    const alarmLynxView = useRef<AlarmLynxViewType>(undefined);
    const compressorLynxView = useRef<CompressorLynxViewType>(undefined);


    const [downloadUid, setDownloadUid] = useState<string>();

    const { data: objectViewState } = useQueryNotifications(useObjectViewState)({
        customerID: userState.customer?.id as number,
        groupID: groupID,
        contextObject: contextObject,
        offline: offlineState.offline
    });

    const { t } = useTranslation();
    const { 
        state, 
        setViewOptionState, 
        clearCounts, 
        setActions,
        setViewMaximized
    } = useSummaryListingContext();

    const noDataMessage = t("summaryListing.nodata", { objectType: 'devices' });

    const { data: availableTabs } = useAvailableSummaryListingTabs({ 
        contextGroupID: groupID,
        contextObject: contextObject,
        enabled: true 
    });


    const hasObjects = state.alarmLynxCount > 0 || 
                        state.compressorLynxCount > 0 || 
                        state.deviceCount > 0 || 
                        state.tankBatteryCount > 0 || 
                        state.wellCount > 0;


    useEffect(() => {
        if(availableTabs) {
            let alternateTab: TabEnum = TabEnum.None;

            alternateTab = alternateTab === TabEnum.None && activeTab <= TabEnum.Devices && availableTabs?.hasDevices ? TabEnum.Devices : alternateTab;
            alternateTab = alternateTab === TabEnum.None && activeTab <= TabEnum.Wells && availableTabs?.hasWells ? TabEnum.Wells : alternateTab;
            alternateTab = alternateTab === TabEnum.None && activeTab <= TabEnum.TankBatteries && availableTabs?.hasTankBatteries ? TabEnum.TankBatteries : alternateTab;
            alternateTab = alternateTab === TabEnum.None && activeTab <= TabEnum.AlarmLynx && availableTabs?.hasAlarmLynx ? TabEnum.AlarmLynx : alternateTab;
            alternateTab = alternateTab === TabEnum.None && activeTab <= TabEnum.CompressorLynx && availableTabs?.hasCompressorLynx ? TabEnum.CompressorLynx : alternateTab;
            alternateTab = alternateTab === TabEnum.None && activeTab > TabEnum.CompressorLynx && availableTabs?.hasCompressorLynx ? TabEnum.CompressorLynx : alternateTab;
            alternateTab = alternateTab === TabEnum.None && activeTab > TabEnum.AlarmLynx && availableTabs?.hasAlarmLynx ? TabEnum.AlarmLynx : alternateTab;
            alternateTab = alternateTab === TabEnum.None && activeTab > TabEnum.TankBatteries && availableTabs?.hasTankBatteries ? TabEnum.TankBatteries : alternateTab;
            alternateTab = alternateTab === TabEnum.None && activeTab > TabEnum.Wells && availableTabs?.hasWells ? TabEnum.Wells : alternateTab;
            alternateTab = alternateTab === TabEnum.None && activeTab > TabEnum.Devices && availableTabs?.hasDevices ? TabEnum.Devices : alternateTab;
    
            if (alternateTab !== activeTab) {
                //setActiveTab(alternateTab, true);
                setActiveTab(alternateTab);
            }
        }
    }, [
        activeTab,
        availableTabs,
        setActiveTab
    ]);

    const onExport = (mimeType: ApplicationMimeTypesEnum) => {

        const devicesExportParameters = devicesView?.current?.getExportParameters();
        const wellsExportParameters = wellsView?.current?.getExportParameters();

        const uid = UUID.generate();
        setDownloadUid(uid);

        actionStarted({
            uid: uid,
            objectID: groupID,
            objectType: eLynxObjectTypeEnum.Group,
            title: objectViewState?.contextObject?.displayText ?? '', 
            notificationMsg: {
                action: ActionTypeEnum.Download,
                notificationID: uid,
                status: StatusEnum.Started,
                statusMessage: t('progress.sendingRequest'),
                additionalInfo: '',
                blobURI: '',
                percentComplete: 0
            }
        });

        const exportOptions: IExportOptions = {
            notificationKey: uid,
            mimeType: mimeType,
            configurationID: devicesExportParameters?.configurationID,
            wellPageConfigID: wellsExportParameters?.configurationID,
            sortBySystemBalanceType: devicesExportParameters?.isSystemBalance ?? false,
            sortByDevicesInAlarmFirst: userState.userSettings?.summaryListingAlarmAtTop ?? false,
            sortByRecentMemo: devicesExportParameters?.sortByRecentMemo ?? false,
            sortAscending: devicesExportParameters?.sortAscending ?? false,
            sortColumnIndex: devicesExportParameters?.sortColumnIndex ?? 0,
            deviceIDs: devicesExportParameters?.objectIDs,
            sortByWellsInAlarmFirst: userState.userSettings?.summaryListingAlarmAtTop ?? false,
            WellIDs: wellsExportParameters?.objectIDs,
            searchTerm: '',
            useCustomOrdering: devicesExportParameters?.useCustomOrdering ?? false
            
        };

        try {

            SummaryListingDataService.exportData(groupID, 
            exportOptions,
            mimeType);


            const items = [...state.actions];
            const exportAction = items.find(o => o.action === BaseActionEnum.Export); 
            if (exportAction) {
                exportAction.disabled = true;
            }
            setActions(items);
        } catch (error) {
            const restError = error as RestError;
            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                show: true,
                error: restError
            });

        }
    }


    // get progress running for current object
    const progress = progressState.items.find(o => o.objectID === groupID && o.objectType === eLynxObjectTypeEnum.Group);

    //console.log(`summarylisting progress: uid: ${progress?.uid} pct: ${progress?.notificationMsg.percentComplete} blob: ${progress?.notificationMsg.blobURI}`)

    function onExecuteViewAction(action: ActionMenuItem) {

        const item = action as ActionMenuItem;

        switch (item.action) {

            case BaseActionEnum.ExpandCollapse:
                setViewMaximized(!state.viewMaximized);
                onExecuteAction?.(action);
                break;

            case BaseActionEnum.Refresh:
                if (activeTab === TabEnum.Devices) {
                    devicesView.current?.executeAction(item.action);
                } else if (activeTab === TabEnum.Wells) {
                    wellsView.current?.executeAction(item.action);
                } else if (activeTab === TabEnum.TankBatteries) {
                    tankBatteryView.current?.executeAction(item.action);
                } else if (activeTab === TabEnum.AlarmLynx) {
                    alarmLynxView.current?.executeAction(item.action);
                } else if (activeTab === TabEnum.CompressorLynx) {
                    compressorLynxView.current?.executeAction(item.action);
                }

                break;

            case BaseActionEnum.Excel:
                if (activeTab === TabEnum.TankBatteries && !isPhone)  {
                    tankBatteryView.current?.executeAction(item.action);
                } else {
                    onExport(ApplicationMimeTypesEnum.MSExcel2007);
                }
                break;

            case BaseActionEnum.PDF: 

                if (activeTab === TabEnum.TankBatteries && !isPhone) {
                    tankBatteryView.current?.executeAction(item.action);
                } else {
                    onExport(ApplicationMimeTypesEnum.PDF);
                }

                break;

    
            default:
                
                if (activeTab === TabEnum.Devices) {
                    devicesView.current?.executeAction(item.action as BaseActionEnum)
                } else if (activeTab === TabEnum.Wells) {
                    wellsView.current?.executeAction(item.action as BaseActionEnum);
                }
                break;
        }
    }


    useEffect(() => {
        if (!availableTabs) {
            clearCounts();
        }
    }, [
        availableTabs,
        clearCounts
    ]);

    useEffect(() => {

        if (objectViewState?.contextObject.id !== groupID ||
            progress?.notificationMsg.status === StatusEnum.Completed ||
            progress?.notificationMsg.status === StatusEnum.Failed) {
            setDownloadUid(undefined);
        }
    }, [
        objectViewState?.contextObject.id,
        groupID,
        progress,
        downloadUid,
        setDownloadUid
    ]);


    useEffect(() => {
        if (!dense) {

            const items = [...state.actions];

            const exportAction = items.find(o => o.action === BaseActionEnum.Export); 

            if (exportAction) {
                exportAction.disabled = (progress && progress.uid === downloadUid) || !hasObjects;
            }

            // if(!_.isEqual(state.actions, items)) {
                setActions(items);
            // }
        }

    }, [
        state.actions,
        progress,
        downloadUid,
        hasObjects,
        setActions,
        dense
    ]);

    useEffect(() => {

        if (!hasObjects) {
            setViewOptionState(ViewOptionStateEnum.Hidden);
        }

    }, [hasObjects,
        setViewOptionState
    ]);

    useEffect(() => {
        if (dense) {
            setActions([{
                label: t("actions.refresh"),
                //icon: () => <Refresh />,
                icon: RefreshIcon,
                action: BaseActionEnum.Refresh,
                order: 0
            }]);
        }
    }, [
        dense,
        setActions,
        t
    ]);

    useEffect(() => {
        setViewMaximized(maximize ?? false);
    },[
        maximize,
        setViewMaximized
    ]);

    // console.log(`SummaryListingDashboardViewContent render ${activeTab}`);

    return (
        <Stack
            direction='column'
            sx={{
                flex: '1 1 auto',

                //margin: designMode ? `${theme.spacing(1)}!important` : undefined,
                //borderRadius: designMode && isSelected ? '4px' : undefined,
                //outline: designMode && isSelected ? 'rgb(42, 198, 180) solid 4px' : undefined,
                overflow: 'hidden'
            }}>
            {
            !designMode ?
                <ContextualAppBar
                    dense={dense}
                    actions={state.actions}
                    viewOptionState={dense ? ViewOptionStateEnum.Hidden : state.viewOptionState}
                    onExecuteAction={onExecuteViewAction}
                    //onOverrideViewOptionsClick={dense ? onViewOptionsClick : undefined}
                    label={title}
                    viewMaximized={state.viewMaximized}
                />
            : 
            <>
            <Divider  />
            <ContextualAppBar 
                dense={dense} 
                viewOptionState={ViewOptionStateEnum.Hidden}
                label={title}
                onExecuteAction={onExecuteAction}
                //actions={dashboardEditorActionItems}
                hideMaximizeViewAction={true}
                sx={{
                    paddingLeft: theme.spacing(1),
                    paddingRight: theme.spacing(2),
                    marginRight: 0,
                    backgroundColor: theme.palette.grey[700]
                }}
            />
            </>

            }
            {/* <Collapse in={!trigger}> */}
            {(availableTabs?.hasDevices || availableTabs?.hasWells ||
                availableTabs?.hasTankBatteries ||
                availableTabs?.hasAlarmLynx || availableTabs?.hasCompressorLynx) &&
                !designMode && !dense &&
                <TabBar
                    value={activeTab}
                    onChange={(_event, value) => setActiveTab(value)}
                >
                    {availableTabs?.hasDevices &&
                        <TabWithCount label={t("summaryListing.tabs.devices") as string}
                            count={state.deviceCount}
                            countDisabled={activeTab !== TabEnum.Devices}
                            value={TabEnum.Devices} />}

                    {availableTabs?.hasWells &&
                        <TabWithCount label={t("summaryListing.tabs.wells") as string}
                            count={state.wellCount}
                            countDisabled={activeTab !== TabEnum.Wells}
                            value={TabEnum.Wells} />}

                    {availableTabs?.hasTankBatteries &&
                        <TabWithCount label={t("summaryListing.tabs.tankBatteries") as string}
                            count={state.tankBatteryCount}
                            countDisabled={activeTab !== TabEnum.TankBatteries}
                            value={TabEnum.TankBatteries} />}

                    {availableTabs?.hasAlarmLynx &&
                        <TabWithCount label={t("summaryListing.tabs.alarmLynx") as string}
                            count={state.alarmLynxCount}
                            countDisabled={activeTab !== TabEnum.AlarmLynx}
                            value={TabEnum.AlarmLynx} />}

                    {availableTabs?.hasCompressorLynx &&
                        <TabWithCount label={t("summaryListing.tabs.compressorLynx") as string}
                            count={state.compressorLynxCount}
                            countDisabled={activeTab !== TabEnum.CompressorLynx}
                            value={TabEnum.CompressorLynx} />}
                </TabBar>
            }
            {availableTabs?.hasDevices &&
                <TabContainer visible={activeTab === TabEnum.Devices}>
                    <DevicesView
                        ref={devicesView}
                        setViewOptionState={setViewOptionState}
                        groupID={groupID}
                        contextObject={contextObject}
                        configurationID={dashboardComponent?.configurationID ?? configurationID}
                        dense={dense}
                        designMode={designMode}

                    />
                </TabContainer>
            }
            {!dense && availableTabs?.hasWells &&
                <TabContainer visible={activeTab === TabEnum.Wells}>
                    <WellsView
                        ref={wellsView}
                        setViewOptionState={setViewOptionState}
                    />
                </TabContainer>
            }
            {!dense && availableTabs?.hasTankBatteries &&
                <TabContainer visible={activeTab === TabEnum.TankBatteries}>
                    <TankBatteryView
                        ref={tankBatteryView}
                        setViewOptionState={setViewOptionState}
                    />
                </TabContainer>
            }
            {!dense && availableTabs?.hasAlarmLynx &&
                <TabContainer visible={activeTab === TabEnum.AlarmLynx}>
                    <AlarmLynxView
                        ref={alarmLynxView}
                        setViewOptionState={setViewOptionState}
                    />
                </TabContainer>
            }
            {!dense && availableTabs?.hasCompressorLynx &&
                <TabContainer visible={activeTab === TabEnum.CompressorLynx}>
                    <CompressorLynxView
                        ref={compressorLynxView}
                        setViewOptionState={setViewOptionState}
                    />
                </TabContainer>
            }
            {
            !dense ? 
                availableTabs && 
                !availableTabs?.hasDevices &&
                !availableTabs?.hasWells &&
                !availableTabs?.hasTankBatteries &&
                !availableTabs?.hasAlarmLynx &&
                !availableTabs?.hasCompressorLynx &&
                    <NoData text={noDataMessage} /> :

                availableTabs && !availableTabs?.hasDevices &&
                    <NoData text={noDataMessage} /> 
            }
        </Stack>
    );
}
